import React, { useState, useEffect } from 'react';
import { X, Cookie, Settings } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';

const CookieConsent = () => {
  const { language } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, can't be disabled
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Show banner after 1 second delay
      setTimeout(() => setIsVisible(true), 1000);
    }
  }, []);

  const handleAcceptAll = () => {
    const consent = {
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    setIsVisible(false);
    
    // Initialize analytics here when accepted
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'granted',
        ad_storage: 'granted'
      });
    }
  };

  const handleRejectAll = () => {
    const consent = {
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    setIsVisible(false);
    
    // Deny analytics
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'denied',
        ad_storage: 'denied'
      });
    }
  };

  const handleSavePreferences = () => {
    const consent = {
      ...preferences,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    setIsVisible(false);
    
    // Update analytics consent
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: preferences.analytics ? 'granted' : 'denied',
        ad_storage: preferences.marketing ? 'granted' : 'denied'
      });
    }
  };

  const togglePreference = (key) => {
    if (key === 'necessary') return; // Can't disable necessary cookies
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (!isVisible) return null;

  const content = {
    ro: {
      title: 'Folosim cookie-uri',
      description: 'Folosim cookie-uri pentru a îmbunătăți experiența ta pe site, pentru a analiza traficul și pentru a personaliza conținutul. Cookie-urile esențiale sunt necesare pentru funcționarea site-ului.',
      acceptAll: 'Acceptă toate',
      rejectAll: 'Respinge opționale',
      customize: 'Personalizează',
      savePreferences: 'Salvează preferințe',
      learnMore: 'Detalii',
      necessary: 'Cookie-uri necesare',
      necessaryDesc: 'Acestea sunt esențiale pentru funcționarea site-ului și nu pot fi dezactivate.',
      analytics: 'Cookie-uri de analiză',
      analyticsDesc: 'Ne ajută să înțelegem cum folosești site-ul pentru a-l îmbunătăți.',
      marketing: 'Cookie-uri de marketing',
      marketingDesc: 'Folosite pentru a-ți arăta conținut relevant și reclame personalizate.'
    },
    en: {
      title: 'We use cookies',
      description: 'We use cookies to improve your experience, analyze traffic, and personalize content. Essential cookies are necessary for the website to function.',
      acceptAll: 'Accept all',
      rejectAll: 'Reject optional',
      customize: 'Customize',
      savePreferences: 'Save preferences',
      learnMore: 'Learn more',
      necessary: 'Necessary cookies',
      necessaryDesc: 'These are essential for the website to function and cannot be disabled.',
      analytics: 'Analytics cookies',
      analyticsDesc: 'Help us understand how you use the website to improve it.',
      marketing: 'Marketing cookies',
      marketingDesc: 'Used to show you relevant content and personalized ads.'
    }
  };

  const t = content[language] || content.ro;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-4 sm:items-center">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={handleRejectAll} />
      
      {/* Modal */}
      <div className="relative w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl sm:p-8">
        {/* Close button */}
        <button
          onClick={handleRejectAll}
          className="absolute right-4 top-4 rounded-full p-2 hover:bg-slate-100"
        >
          <X className="h-5 w-5 text-slate-500" />
        </button>

        {/* Icon */}
        <div className="mb-4 flex items-center gap-3">
          <div className="rounded-full bg-blue-100 p-3">
            <Cookie className="h-6 w-6 text-blue-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900">{t.title}</h2>
        </div>

        {/* Description */}
        <p className="mb-6 text-slate-600">
          {t.description}{' '}
          <a href="/legal/privacy-policy" className="text-blue-600 hover:underline">
            {t.learnMore}
          </a>
        </p>

        {/* Settings Panel */}
        {showSettings && (
          <div className="mb-6 space-y-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
            {/* Necessary Cookies */}
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-slate-900">{t.necessary}</h3>
                  <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                    {language === 'ro' ? 'Întotdeauna active' : 'Always active'}
                  </span>
                </div>
                <p className="mt-1 text-sm text-slate-600">{t.necessaryDesc}</p>
              </div>
            </div>

            {/* Analytics Cookies */}
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900">{t.analytics}</h3>
                <p className="mt-1 text-sm text-slate-600">{t.analyticsDesc}</p>
              </div>
              <button
                onClick={() => togglePreference('analytics')}
                className={`ml-4 h-6 w-11 rounded-full transition-colors ${
                  preferences.analytics ? 'bg-blue-600' : 'bg-slate-300'
                }`}
              >
                <div
                  className={`h-5 w-5 rounded-full bg-white shadow-md transition-transform ${
                    preferences.analytics ? 'translate-x-6' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>

            {/* Marketing Cookies */}
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900">{t.marketing}</h3>
                <p className="mt-1 text-sm text-slate-600">{t.marketingDesc}</p>
              </div>
              <button
                onClick={() => togglePreference('marketing')}
                className={`ml-4 h-6 w-11 rounded-full transition-colors ${
                  preferences.marketing ? 'bg-blue-600' : 'bg-slate-300'
                }`}
              >
                <div
                  className={`h-5 w-5 rounded-full bg-white shadow-md transition-transform ${
                    preferences.marketing ? 'translate-x-6' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col gap-3 sm:flex-row">
          {!showSettings ? (
            <>
              <Button
                onClick={handleAcceptAll}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                {t.acceptAll}
              </Button>
              <Button
                onClick={() => setShowSettings(true)}
                variant="outline"
                className="flex-1"
              >
                <Settings className="mr-2 h-4 w-4" />
                {t.customize}
              </Button>
              <Button
                onClick={handleRejectAll}
                variant="ghost"
                className="flex-1"
              >
                {t.rejectAll}
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={handleSavePreferences}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                {t.savePreferences}
              </Button>
              <Button
                onClick={() => setShowSettings(false)}
                variant="outline"
                className="flex-1"
              >
                {language === 'ro' ? 'Înapoi' : 'Back'}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
